import {
    loadAnimationBaseBytes,
    loadBodyPartModelBytes,
    loadCharacterModelBytes,
    loadEquipmentModelBytes,
    loadAssetManifest,
    versionedAssetUrl,
} from '@sorrem/engine';
import { createLogger, type AssetManifest, type AssetManifestEntry, type AssetManifestModel, type AssetPrecacheTier } from '@sorrem/shared';

const logger = createLogger('client:asset-precache');

/** How many files are fetched at once: enough to fill the connection, not so many the screen's own loads wait. */
const PRECACHE_CONCURRENCY = 6;

/** Where precaching is: not started, caching the core tier (entry waits for it), the run tier (in the background), or done. */
export type AssetPrecachePhase = 'idle' | 'core' | 'run' | 'done';

export interface AssetPrecacheState {
    readonly phase: AssetPrecachePhase;
    /** Files of the current tier fetched so far, and how many it has. */
    readonly done: number;
    readonly total: number;
}

/** How each kind of file is fetched into the client's caches. */
export interface AssetPrecacheFetchers {
    /** A catalog model, into the model cache (IndexedDB). */
    model(model: AssetManifestModel): Promise<unknown>;
    /** Any other file, into the HTTP cache by its versioned URL. */
    file(path: string): Promise<unknown>;
}

/** A tier's files, smallest first, so the count moves quickly and the big models come last. */
export function selectPrecacheEntries(manifest: AssetManifest, tier: AssetPrecacheTier): AssetManifestEntry[] {
    return manifest.entries.filter((entry) => entry.tier === tier).sort((a, b) => a.sizeBytes - b.sizeBytes);
}

/**
 * Fetches each entry into the client's caches, a few at a time, telling
 * `onProgress` after each. A file that fails is logged and skipped: it is
 * fetched again when first used.
 */
export async function precacheEntries(
    entries: readonly AssetManifestEntry[],
    fetchers: AssetPrecacheFetchers,
    onProgress: (done: number) => void,
    concurrency = PRECACHE_CONCURRENCY,
): Promise<void> {
    let next = 0;
    let done = 0;
    const worker = async (): Promise<void> => {
        while (next < entries.length) {
            const entry = entries[next++]!;
            try {
                if (entry.models !== undefined && entry.models.length > 0) {
                    // Each id is its own cache entry (several can share a file).
                    await Promise.all(entry.models.map((model) => fetchers.model(model)));
                } else {
                    await fetchers.file(entry.path);
                }
            } catch (error) {
                logger.warn(`could not precache ${entry.path}; it loads on first use:`, error);
            }
            onProgress(++done);
        }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, worker));
}

/** The game's own fetchers: models through the engine's model cache, files by versioned URL into the HTTP cache. */
const GAME_FETCHERS: AssetPrecacheFetchers = {
    model: (model) => {
        switch (model.kind) {
            case 'character':
                return loadCharacterModelBytes(model.id);
            case 'equipment':
                return loadEquipmentModelBytes(model.id);
            case 'body-part':
                return loadBodyPartModelBytes(model.id);
            case 'animation':
                return loadAnimationBaseBytes(model.id);
        }
    },
    file: async (path) => {
        const response = await fetch(versionedAssetUrl(path));
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        // Read through, so the response is stored whole; the bytes themselves aren't kept here.
        await response.arrayBuffer();
    },
};

/**
 * Precaching for the page (#285): once per page session, on entering
 * Sorrem, the core tier (entry waits for it), then the run tier in the
 * background. A warm visit finds everything cached and is through in
 * moments; a later "Returning home" finds it done.
 */
class AssetPrecache {
    private state: AssetPrecacheState = { phase: 'idle', done: 0, total: 0 };
    private readonly listeners = new Set<() => void>();
    private started = false;

    readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    readonly getState = (): AssetPrecacheState => this.state;

    /** Starts precaching, once; later calls do nothing. */
    start(fetchers: AssetPrecacheFetchers = GAME_FETCHERS): void {
        if (this.started) {
            return;
        }
        this.started = true;
        void this.run(fetchers);
    }

    private async run(fetchers: AssetPrecacheFetchers): Promise<void> {
        const manifest = await loadAssetManifest();
        if (manifest === null) {
            this.set({ phase: 'done', done: 0, total: 0 });
            return;
        }
        for (const tier of ['core', 'run'] as const) {
            const entries = selectPrecacheEntries(manifest, tier);
            this.set({ phase: tier, done: 0, total: entries.length });
            await precacheEntries(entries, fetchers, (done) => this.set({ phase: tier, done, total: entries.length }));
        }
        this.set({ phase: 'done', done: 0, total: 0 });
    }

    private set(state: AssetPrecacheState): void {
        this.state = state;
        for (const listener of this.listeners) {
            listener();
        }
    }
}

let precache: AssetPrecache | null = null;

/** The page's precaching, made on first use. */
export function getAssetPrecache(): AssetPrecache {
    precache ??= new AssetPrecache();
    return precache;
}
